import { useCallback, useEffect, useState } from "react";

export interface AsyncState<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
  /** Bump this to re-run `fn` (e.g. after a mutation succeeds). */
  reload: () => void;
}

/**
 * Small shared data-fetching hook so every page in this app doesn't hand-roll the same
 * loading/error/reload boilerplate around `ensureFreshToken()` + an `api/*Api.ts` call.
 * Deliberately minimal (no caching layer, no request de-duplication, no stale-while-
 * revalidate) — this app's task brief calls out "server-side caching hooks" as one of
 * §5's Accountant Control Panel requirements, but a full cache layer is out of scope for
 * a "solid functional core" pass; this hook is the seam a real one (e.g. a
 * TanStack-Query-backed replacement) would slot into later without touching call sites.
 */
export function useAsyncData<T>(fn: () => Promise<T>, deps: readonly unknown[]): AsyncState<T> {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    fn()
      .then((result) => {
        if (!cancelled) setData(result);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof Error ? err.message : "Failed to load");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, reloadKey]);

  const reload = useCallback(() => setReloadKey((key) => key + 1), []);

  return { data, loading, error, reload };
}
